import React, { useCallback, useRef, useState, useEffect } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { groupBy, isEmpty, filter, without, omit, uniqBy } from 'lodash-es';

import InfiniteScroll from 'react-infinite-scroll-component';

import {
  MenuItem,
  ShowOnlySelected,
  MenuHeader,
  MenuDivider,
  MenuGroup,
  SubMenu,
  CustomLabelCheckbox,
  useSubMenuProps,
  useShadowLine,
  useSearchOnMenuOpen,
} from '../../../../DropdownSet';
import SearchField from '../../../../Search/SearchField';
import LoadingSpinner from '../../../../LoadingSpinner';
import AppIcon from '../../../../AppIcon';
import StoreIcon from '../../../../StoreIcon';

import api from '../../../api';
import { unifiedRefNoBy } from '../../../../../utils/helpers';
import { CROSS_APP_PANEL_VERSION_MAPPING } from '../../../../../utils/advanced-metrics';

const infiniteScrollStyles = { overflow: 'visible' };

const renderOptionLabel = (app, { size = 24, className } = {}) => (
  <div className={cx('paired-apps-dropdown-label d-flex align-items-center', className)}>
    <StoreIcon store={app.store} />
    <AppIcon
      alt={app.name}
      url={app.icon_url}
      store={app.store}
      size={size}
      hideHtmlTitle
      withBorder
      containerClasses="paired-apps-dropdown-label__icon"
    />
    <span className="paired-apps-dropdown-label__name text-truncate">{app.name}</span>
  </div>
);

const CrossAppPairingsSubMenu = ({
  selected,
  app = {},
  groupAppsBy = 'sector',
  panelVersion,
  currentRefNo,
  crossAppsPairsDecorated,
  onChange,
  onCurrentRefNoChange,
}) => {
  const subMenuInstanceRef = useRef();
  const subMenuProps = useSubMenuProps({
    menuStyle: { width: 401 },
    gap: 0,
  });
  const [shadowLine, onScroll] = useShadowLine();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSelected, setShowSelected] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchOptions, setSearchOptions] = useState([]);
  const [localTotal, setTotal] = useState(0);
  const [localOffset, setOffset] = useState(0);
  const [cachedSelected, setCachedSelected] = useState({});

  const fetchCrossAppsDataRequest = (ref_no, offset, query) => {
    setError(false);
    setLoading(true);
    api
      .fetchCrossAppsData({
        params: {
          unified_ref_no: ref_no,
          offset: offset || 0,
          limit: 20,
          query,
          panel_version: CROSS_APP_PANEL_VERSION_MAPPING[panelVersion],
        },
      })
      .then(({ data: { related_apps_list, total, next_offset } }) => {
        setLoading(false);
        setTotal(total);
        setOffset(next_offset);
        const unifiedRefNosList = related_apps_list.map((related_app) => ({
          ...related_app,
          ref_no: unifiedRefNoBy({ ...related_app, kind: 'app' }),
        }));
        setSearchOptions((prev) => uniqBy([...prev, ...unifiedRefNosList], 'ref_no'));
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  };

  const { searchInstanceRef, onMenuChange: onSearchMenuChange } = useSearchOnMenuOpen();

  const handlePairsSearch = ({ open }, value) => {
    onSearchMenuChange({ open });

    if (!open || value.ref_no === currentRefNo) return;

    onCurrentRefNoChange(value.ref_no);
    setShowSelected(false);
    setSearchOptions([]);
    setTotal(0);
    setOffset(0);
    fetchCrossAppsDataRequest(value.ref_no, 0, '');
  };

  const handleSearchChange = (value) => {
    setShowSelected(false);
    setSearchOptions([]);
    setTotal(0);
    setOffset(0);
    setSearchQuery(value);
    fetchCrossAppsDataRequest(currentRefNo, 0, value);
  };

  const handleSelectedChange = (selectedApp, action) => {
    setCachedSelected((prev) => {
      let newSelected = [];
      const prevApps = prev[currentRefNo] || [];
      if (action === 'add') {
        newSelected = [...prevApps, selectedApp];
      } else if (action === 'remove') {
        newSelected = filter(prevApps, (option) => selectedApp.ref_no !== option.ref_no);
      }
      return {
        ...omit(prev, [currentRefNo]),
        [currentRefNo]: newSelected,
      };
    });
  };

  useEffect(() => {
    setShowSelected(false);
    setSearchOptions([]);
    setTotal(0);
  }, [panelVersion]);

  useEffect(() => {
    if (
      crossAppsPairsDecorated &&
      crossAppsPairsDecorated[currentRefNo] &&
      !cachedSelected[currentRefNo]
    ) {
      const preSelected = crossAppsPairsDecorated[currentRefNo].filter((option) =>
        selected[currentRefNo].includes(option.unified_app_id)
      );
      setCachedSelected((prev) => ({ ...omit(prev, [currentRefNo]), [currentRefNo]: preSelected }));
    }
  }, [crossAppsPairsDecorated, currentRefNo]);

  const handleChange = (e, value, selectedApp) => {
    e.keepOpen = true;
    const presentAppPair = selected[currentRefNo];
    if (!presentAppPair) {
      const list = { ...selected, [currentRefNo]: [value] };
      handleSelectedChange(selectedApp, 'add');
      return onChange(list);
    }

    let subList;
    let action;
    if (selected[currentRefNo].includes(value)) {
      subList = without(selected[currentRefNo], value);
      action = 'remove';
    } else {
      subList = [...selected[currentRefNo], value];
      action = 'add';
    }

    const mainList = !isEmpty(subList)
      ? { ...selected, [currentRefNo]: subList }
      : omit(selected, [currentRefNo]);

    onChange(mainList);
    handleSelectedChange(selectedApp, action);
  };

  let nextSelectedOptions = [];
  if (showSelected && !isEmpty(selected[currentRefNo])) {
    if (cachedSelected[currentRefNo] && cachedSelected[currentRefNo].length > 0) {
      nextSelectedOptions = cachedSelected[currentRefNo];
    }
  } else if (searchQuery === '' && panelVersion === 'us-blended-v1-r0-0') {
    if (cachedSelected[currentRefNo] && cachedSelected[currentRefNo].length > 0) {
      nextSelectedOptions = cachedSelected[currentRefNo];
    }
  } else {
    nextSelectedOptions = searchOptions;
  }

  const renderMenuOptions = (renderOptions) => {
    const renderMenuOption = (option, index) => {
      const { name, ref_no, disabled: isDisabled = false, unified_app_id } = option;
      const isChecked = unified_app_id
        ? selected[currentRefNo]?.includes(unified_app_id)
        : selected[currentRefNo]?.includes(ref_no);

      return (
        <MenuItem
          key={ref_no || index}
          value={ref_no}
          disabled={isDisabled}
          onClick={(e) => handleChange(e, unified_app_id || ref_no, option)}
        >
          <CustomLabelCheckbox
            id={name}
            disabled={isDisabled}
            checked={!!isChecked}
            labelText={renderOptionLabel(option, { className: 'paired-apps-dropdown-label--menu' })}
          />
        </MenuItem>
      );
    };

    if (groupAppsBy) {
      const groupedOptions = groupBy(renderOptions, groupAppsBy);
      const groupLabels = Object.keys(groupedOptions);

      return groupLabels.reduce((acc, groupLabel, index) => {
        const isLast = index === groupLabels.length - 1;

        return acc.concat(
          <MenuHeader key={groupLabel}>{groupLabel}</MenuHeader>,
          groupedOptions[groupLabel].map(renderMenuOption),
          !isLast ? <MenuDivider key={`${groupLabel}-divider`} fullWidth dense /> : []
        );
      }, []);
    }

    return renderOptions.map(renderMenuOption);
  };

  const handleLoadMore = (ref_no, offset) => {
    fetchCrossAppsDataRequest(ref_no, localOffset || offset, searchQuery);
  };

  const toggleShowSelected = useCallback((e) => {
    e.keepOpen = true;
    setShowSelected(e.checked);
  }, []);

  const isSearchActive = searchQuery.trim().length > 0;

  const fullRefNo = unifiedRefNoBy({ ...app, kind: 'app' });
  const scrollId = `paired-apps-menu-group-${fullRefNo}`;

  const handlePairsSearchRef = useRef();
  handlePairsSearchRef.current = (event) =>
    handlePairsSearch(event, { ...app, kind: 'app', ref_no: fullRefNo });
  // Must keep a stable identity to avoid yanking focus off the search field on every keystroke.
  const handleSubMenuChange = useCallback((event) => handlePairsSearchRef.current(event), []);

  const noResults = !nextSelectedOptions.length;

  return (
    <SubMenu
      key={app.value}
      ref={subMenuInstanceRef}
      label={renderOptionLabel(app)}
      {...subMenuProps}
      onMenuChange={handleSubMenuChange}
    >
      <>
        <SearchField instanceRef={searchInstanceRef} onChange={handleSearchChange} />
        <MenuDivider key="divider" fullWidth dense />
        {!isSearchActive && (
          <ShowOnlySelected
            checked={showSelected}
            disabled={isEmpty(selected[fullRefNo])}
            onClick={toggleShowSelected}
          />
        )}
        <MenuDivider key="divider-2" fullWidth dense className="m-b-0" />
        {shadowLine}
        {!nextSelectedOptions?.length && loading && (
          <div className="paired-apps-dropdown-initial">
            <LoadingSpinner
              size="lg"
              id="paired-apps-dropdown-initial"
              className="text-blue justify-content-center"
            />
          </div>
        )}
        {!noResults && (
          <MenuGroup
            takeOverflow
            id={scrollId}
            className="paired-apps-dropdown__menu-group"
            style={{ height: 600 }}
            onScroll={onScroll}
          >
            <InfiniteScroll
              dataLength={nextSelectedOptions?.length || 0}
              hasMore={nextSelectedOptions?.length <= localTotal}
              scrollThreshold={0.95}
              scrollableTarget={scrollId}
              style={infiniteScrollStyles}
              className="paired-apps-dropdown__list"
              next={() => handleLoadMore(fullRefNo, nextSelectedOptions.length)}
            >
              {renderMenuOptions(nextSelectedOptions)}
              {loading && (
                <LoadingSpinner
                  size="sm"
                  id="paired-apps-dropdown-more"
                  className="text-blue justify-content-center"
                />
              )}
            </InfiniteScroll>
          </MenuGroup>
        )}
        {error && (
          <div className="text-danger text-center p-3">Error occurred while loading apps</div>
        )}
        {!loading && noResults && <div className="text-center p-3">No results found</div>}
      </>
    </SubMenu>
  );
};

CrossAppPairingsSubMenu.propTypes = {
  selected: T.object,
  app: T.object,
  options: T.array,
  disabled: T.bool,
  panelVersion: T.string,
  groupAppsBy: T.string,
  currentRefNo: T.string,
  unselectedStateText: T.string,
  onChange: T.func,
  onCurrentRefNoChange: T.func,
  crossAppsPairsDecorated: T.object,
};

export default CrossAppPairingsSubMenu;
